import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {ReviewerType} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import notifications from 'notifications';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT, RED} from 'ui-library/Colors';
import AddIcon from 'ui-library/icons/AddIcon';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {FieldRequiredIndicator} from 'ui-library/inputs/FieldLabel';
import {OptionData} from 'ui-library/menus/MenuConstants';
import ReorderableList, {ItemData} from 'ui-library/reorderable/ReorderableList';
import ReorderableSimpleTableRow, {
  ReorderableSimpleTableRowProps,
} from 'ui-library/reorderable/ReorderableSimpleTableRow';
import APIUtil from 'util/APIUtil';

import {createBlankStageNode} from './ReviewLifecycleConfigurationEditor';
import {areTeamReviewsEnabled, isAuthorReviewInReviewLifecyclesEnabled} from './ReviewLifecyclesUtil';
import ReviewStageDropdown from './ReviewStageDropdown';
import ReviewStageTextField from './ReviewStageTextField';

export const REVIEW_STAGE_TABLE_COMPONENT = 'ReviewStageTable';

export const REVIEW_STAGE_LIMIT = 20;

export interface ReviewLifecycleStageNode {
  name: string;
  reviewerType: OptionData;
  isTeamReassignable: boolean | null;
  actionLabel: OptionData;
  definition: string;
  clientId: string;
  previousVersionReviewLifecycleStageId: string | null;
}

interface ReorderableListContainerProps {
  stageNodes: Array<ReviewLifecycleStageNode>;
  setName: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, name: string) => void;
  removeReviewStage: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number) => void;
  setIsTeamReassignable: (
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    isTeamReassignable: boolean
  ) => void;
  setDefinition: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, definition: string) => void;
  setReviewerType: (
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    reviewerType: OptionData
  ) => void;
  setActionLabel: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, actionLabel: OptionData) => void;
  reorderStages: (newOrderClientIds: Array<string>) => void;
  actionLabelOptions: Array<OptionData>;
  reviewerTypeOptions: Array<OptionData>;
  disabled?: boolean;
}

export function ReorderableListContainer({
  stageNodes,
  setName,
  removeReviewStage,
  setIsTeamReassignable,
  setDefinition,
  setReviewerType,
  setActionLabel,
  reorderStages,
  actionLabelOptions,
  reviewerTypeOptions,
  disabled,
}: ReorderableListContainerProps): JSX.Element {
  const getListItemForStageNode = (
    nodeConfig: ReviewLifecycleStageNode,
    idx: number
  ): ItemData<ReorderableSimpleTableRowProps> => ({
    id: nodeConfig.clientId,
    itemClass: ReorderableSimpleTableRow,
    itemProps: {
      isDraggingDisabled: Boolean(disabled),
      rowContainerClassName: css(styles.rowContainer),
      dragHandleClassName: css(styles.dragHandle),
      row: {
        id: nodeConfig.clientId,
        cells: [
          {
            key: `${nodeConfig.clientId}-name`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                {disabled ? (
                  <div>{nodeConfig.name}</div>
                ) : (
                  <ReviewStageTextField
                    fieldValue={nodeConfig.name}
                    handleSelectionChange={(name) => {
                      setName(stageNodes, idx, name);
                    }}
                    dataTestAttrs={{
                      component: REVIEW_STAGE_TABLE_COMPONENT,
                      element: 'reviewStageName',
                    }}
                  />
                )}
              </div>
            ),
          },
          {
            key: `${nodeConfig.clientId}-reviewerType`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                {disabled ? (
                  <div>{nodeConfig.reviewerType.name}</div>
                ) : (
                  <ReviewStageDropdown
                    selectedDropdownOption={nodeConfig.reviewerType}
                    allDropdownOptions={reviewerTypeOptions}
                    onSelectDropdownOption={(reviewerType: OptionData) => {
                      assert(
                        reviewerType.value !== ReviewerType.AUTHOR ||
                          isAuthorReviewInReviewLifecyclesEnabled(),
                        'Cannot select author as reviewer if self-reviews are not enabled'
                      );
                      assert(
                        reviewerType.value !== ReviewerType.TEAM || areTeamReviewsEnabled(),
                        'Cannot select a team as a reviewer if team reviews are not enabled'
                      );
                      setReviewerType(stageNodes, idx, reviewerType);
                    }}
                    dataTestAttrs={{
                      component: REVIEW_STAGE_TABLE_COMPONENT,
                      element: 'reviewerTypeDropdown',
                    }}
                  />
                )}
              </div>
            ),
          },
          {
            key: `${nodeConfig.clientId}-reassignable`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                <CheckboxInput
                  label=""
                  id="enable-team-reassign"
                  checked={stageNodes[idx].isTeamReassignable ?? false}
                  disabled={disabled || !APIUtil.isTeam(stageNodes[idx].reviewerType.value)}
                  onChange={(e) => {
                    setIsTeamReassignable(stageNodes, idx, e.target.checked);
                  }}
                />
              </div>
            ),
          },
          {
            key: `${nodeConfig.clientId}-actionLabel`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                {disabled ? (
                  <div>{nodeConfig.actionLabel.name}</div>
                ) : (
                  <ReviewStageDropdown
                    selectedDropdownOption={nodeConfig.actionLabel}
                    allDropdownOptions={actionLabelOptions}
                    onSelectDropdownOption={(actionLabel: OptionData) => {
                      setActionLabel(stageNodes, idx, actionLabel);
                    }}
                    dataTestAttrs={{
                      component: REVIEW_STAGE_TABLE_COMPONENT,
                      element: 'actionLabelDropdown',
                    }}
                  />
                )}
              </div>
            ),
          },
          {
            key: `${nodeConfig.clientId}-definition`,
            contents: (
              <div className={css(styles.selectItemContainer, styles.dropdownContainer)}>
                {disabled ? (
                  <div>{nodeConfig.definition}</div>
                ) : (
                  <ReviewStageTextField
                    fieldValue={nodeConfig.definition}
                    handleSelectionChange={(definition) => {
                      setDefinition(stageNodes, idx, definition);
                    }}
                    dataTestAttrs={{
                      component: REVIEW_STAGE_TABLE_COMPONENT,
                      element: 'reviewStageDefinition',
                    }}
                  />
                )}
              </div>
            ),
          },
          {
            key: `${nodeConfig.clientId}-delete`,
            cellClassName: 'simpleDivTable-actionGroupContainer',
            contents: (
              <div className={css(styles.buttonContainer)}>
                {disabled ? null : (
                  <TooltipWrapper tooltipTitle="Remove review stage">
                    <Button
                      buttonStyle={ButtonStyle.LINK}
                      className={classNames(css(styles.singleButton), css(styles.deleteButton))}
                      icon="delete"
                      onClick={() => {
                        removeReviewStage(stageNodes, idx);
                      }}
                      dataTestAttrs={{
                        component: REVIEW_STAGE_TABLE_COMPONENT,
                        element: 'removeReviewStage',
                      }}
                    />
                  </TooltipWrapper>
                )}
              </div>
            ),
          },
        ],
      },
    },
  });

  return (
    <ReorderableList
      itemDatas={stageNodes.map((stageNode, idx) => getListItemForStageNode(stageNode, idx))}
      onMoveItem={(updatedStageNodes: Array<string>) => {
        reorderStages(updatedStageNodes);
      }}
      onMoveItemEnd={noop}
    />
  );
}

export interface ReviewStageTableProps {
  stageNodes: Array<ReviewLifecycleStageNode>;
  onUpdateReviewStages: (stageNodes: Array<ReviewLifecycleStageNode>) => void;
  setName: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, name: string) => void;
  removeReviewStage: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number) => void;
  setDefinition: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, definition: string) => void;
  setReviewerType: (
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    reviewerType: OptionData
  ) => void;
  setIsTeamReassignable: (
    stageNodes: Array<ReviewLifecycleStageNode>,
    idx: number,
    isTeamReassignable: boolean
  ) => void;
  setActionLabel: (stageNodes: Array<ReviewLifecycleStageNode>, idx: number, actionLabel: OptionData) => void;
  reorderStages: (newOrderClientIds: Array<string>) => void;
  actionLabelsMenuOptions: Array<OptionData>;
  reviewerTypeMenuOptions: Array<OptionData>;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
}

export const REVIEW_STAGE_NAME_HELP_TEXT =
  'The name of the stage that will be shown throughout the review experience in entries, audit logs, etc.';
export const REVIEWER_HELP_TEXT = 'The set of users who can be selected to review a stage in an entry.';
export const REVIEW_REASSIGNABLE_HELP_TEXT = `Checking this box allows for stage reviewer reassignment: team members can reassign the review stage to other team members. Only configurable for team-based review stages.`;
export const REVIEW_ACTION_HELP_TEXT = 'The button label a reviewer will click when completing the stage.';
export const DEFINITION_HELP_TEXT =
  'Shown to Reviewers when completing a stage. Can be used to better define the meaning of the signature, link to documents/policies, etc.';
export const HELP_ICON_CLASS_NAME = 'tagSchemaSettings-helpIcon u-tiny-spacing-left';

export function ReviewStageTable({
  stageNodes,
  onUpdateReviewStages,
  setName,
  removeReviewStage,
  setIsTeamReassignable,
  setDefinition,
  setReviewerType,
  setActionLabel,
  reorderStages,
  actionLabelsMenuOptions,
  reviewerTypeMenuOptions,
  disabled,
}: ReviewStageTableProps): JSX.Element {
  return (
    <div className={css(styles.listWrapper)}>
      <div className={classNames('simpleDivTable-header', css(styles.selectItemContainer))}>
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Review Stage Name
          {!disabled && (
            <>
              <FieldRequiredIndicator />
              <HelpButton
                renderContent={() => REVIEW_STAGE_NAME_HELP_TEXT}
                iconClassName={HELP_ICON_CLASS_NAME}
              />
              <AddIcon
                onClick={() => {
                  if (stageNodes.length >= REVIEW_STAGE_LIMIT) {
                    notifications.error('Review process cannot have more than 20 stages.');
                  } else {
                    onUpdateReviewStages([...stageNodes, createBlankStageNode(reviewerTypeMenuOptions)]);
                  }
                }}
                dataTestAttrs={{
                  component: 'ReviewStageTable',
                  element: 'addRow',
                }}
              />
            </>
          )}
        </div>
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Reviewer
          {!disabled && (
            <>
              <FieldRequiredIndicator />
              <HelpButton renderContent={() => REVIEWER_HELP_TEXT} iconClassName={HELP_ICON_CLASS_NAME} />
            </>
          )}
        </div>
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Reassignable
          {!disabled && (
            <>
              <HelpButton
                renderContent={() => REVIEW_REASSIGNABLE_HELP_TEXT}
                iconClassName={HELP_ICON_CLASS_NAME}
              />
            </>
          )}
        </div>
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Action Label
          {!disabled && (
            <>
              <FieldRequiredIndicator />
              <HelpButton
                renderContent={() => REVIEW_ACTION_HELP_TEXT}
                iconClassName={HELP_ICON_CLASS_NAME}
              />
            </>
          )}
        </div>
        <div className="simpleDivTable-headerCell simpleDivTable-header--title">
          Definition
          <HelpButton renderContent={() => DEFINITION_HELP_TEXT} iconClassName={HELP_ICON_CLASS_NAME} />
        </div>
      </div>
      <ReorderableListContainer
        stageNodes={stageNodes}
        setName={setName}
        setIsTeamReassignable={setIsTeamReassignable}
        setDefinition={setDefinition}
        setActionLabel={setActionLabel}
        setReviewerType={setReviewerType}
        reorderStages={reorderStages}
        removeReviewStage={removeReviewStage}
        actionLabelOptions={actionLabelsMenuOptions}
        reviewerTypeOptions={reviewerTypeMenuOptions}
        disabled={disabled}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  dragHandle: {
    marginRight: 0,
    width: '16px',
  },
  buttonContainer: {
    marginLeft: '15px',
  },
  singleButton: {
    marginLeft: '6px',
    padding: '0px',
    boxShadow: 'none',
  },
  rowContainer: {
    marginLeft: '-16px',
  },
  selectItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  listWrapper: {
    border: `1px solid ${GRAY_LIGHT}`,
  },
  deleteButton: {
    paddingLeft: '8px',
    paddingRight: '8px',
    color: GRAY,
    ':hover': {
      color: RED,
    },
  },
});
